import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wifi, Snowflake, Car, UtensilsCrossed, Star, MapPin, Mountain,
  Waves, Trees, Sparkles, CalendarDays, Users, ArrowRight, Loader2, XCircle, BookOpen, Tag, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg, optimizeRemote } from "../lib/api";
import { SITE_URL, BUSINESS, aptUrl } from "../lib/site";
import { nextDay, onCheckinChange } from "../lib/dates";
import ConciergeWidget from "../components/ConciergeWidget";
import BookingModal from "../components/BookingModal";
import WeatherWidget from "../components/WeatherWidget";
import Seo from "../components/Seo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useI18n } from "../context/I18nContext";

const HERO = "https://images.unsplash.com/photo-1628087236657-0cc963ad15fd?crop=entropy&cs=srgb&fm=webp&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxCcmVudGElMjBEb2xvbWl0ZXMlMjBtb3VudGFpbnMlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzg0MzU2MTcyfDA&ixlib=rb-4.1.0&q=80";
const SKI = "https://images.pexels.com/photos/12178581/pexels-photo-12178581.jpeg?auto=compress&cs=tinysrgb&dpr=2&fm=webp&h=650&w=940";
const RAFT = "https://images.unsplash.com/photo-1629248457649-b082812aea6c?crop=entropy&cs=srgb&fm=webp&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxyaXZlciUyMHJhZnRpbmd8ZW58MHx8fHwxNzg0MzU2MTcyfDA&ixlib=rb-4.1.0&q=80";
const INTERIOR = "https://images.unsplash.com/photo-1761470532026-322f58233efc?crop=entropy&cs=srgb&fm=webp&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhbHBpbmUlMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMHdvb2R8ZW58MHx8fHwxNzg0MzU2MTcyfDA&ixlib=rb-4.1.0&q=80";

const AMENITY_ICONS = {
  "Wi-Fi veloce": Wifi, "Deposito sci riscaldato": Snowflake, "Parcheggio privato": Car,
  "Cucina attrezzata": UtensilsCrossed,
};

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const DEFAULT_CONTENT = {
  hero_eyebrow: "Val di Sole · Trentino",
  hero_title: "Appartamenti in",
  hero_title_highlight: "Val di Sole",
  hero_subtitle: "Alloggi di charme ai piedi delle Dolomiti di Brenta, a due passi dagli impianti di Folgarida-Marilleva.",
  hero_image: HERO,
  story_eyebrow: "La tua base in montagna",
  story_title: "Dove le stagioni diventano ricordi",
  story_p1: "",
  story_p2: "",
  story_location: "Dimaro Folgarida, Val di Sole (TN)",
  story_image: INTERIOR,
  rating_value: "4.9",
  apartments_eyebrow: "Il nostro alloggio",
  apartments_title: "Comfort tra i monti",
  experiences_eyebrow: "Quattro stagioni · Convenzionati",
  experiences_title: "Cosa fare a Dimaro",
  experiences_subtitle: "Esperienze selezionate con i nostri partner convenzionati e sconti riservati ai nostri ospiti.",
  reviews_eyebrow: "Social proof",
  reviews_title: "Le voci dei nostri ospiti",
  footer_tagline: "Appartamento di charme in Val di Sole. Il tuo soggiorno alpino, tutto l'anno.",
  contact_email: "appartamentovaldisole@gmail.com",
  contact_address: "Via delle Dolomiti, Dimaro (TN)",
  map_query: "Dimaro,Val di Sole,Trentino",
};

const HOME_FAQ = [
  { q: "Dove si trovano gli appartamenti?", a: "A Dimaro Folgarida, in Val di Sole (Trentino), ai piedi delle Dolomiti di Brenta e a pochi minuti dagli impianti di Folgarida-Marilleva." },
  { q: "Quali strutture posso prenotare?", a: "Appartamenti indipendenti con cucina, Wi-Fi e parcheggio, ideali per coppie e famiglie." },
  { q: "Conviene prenotare dal sito ufficiale?", a: "Sì: prenotando su valdisoleapartments.it hai il miglior prezzo senza commissioni e il contatto diretto con il proprietario." },
  { q: "Cosa si può fare in zona?", a: "In inverno sci e ciaspole a Folgarida-Marilleva; in estate rafting sul fiume Noce, e-bike e trekking nel Parco Naturale Adamello-Brenta." },
  { q: "Quanto dista dagli impianti e dai punti di interesse?", a: "Gli impianti di Folgarida-Marilleva e la cabinovia di Daolasa sono a pochi minuti d'auto; Madonna di Campiglio si raggiunge in circa 25-30 minuti e le terme di Pejo e Rabbi in circa 20-30 minuti." },
  { q: "C'è la ricarica per auto elettriche (EV)?", a: "In zona sono disponibili colonnine di ricarica per auto elettriche; contattaci per indicazioni sulla più vicina alla struttura." },
  { q: "È adatto alle famiglie con bambini?", a: "Sì: gli appartamenti offrono cucina e spazi comodi per le famiglie, e la zona propone piste facili e scuole di sci d'inverno e attività per bambini d'estate." },
  { q: "Come funziona l'arrivo e il check-in?", a: "Dopo la prenotazione ti inviamo tutte le istruzioni di arrivo; le informazioni di accesso (Wi-Fi e codici) vengono comunicate a partire da 2 giorni prima del check-in." },
];

const SEO_LINKS = [
  { slug: "appartamenti-a-dimaro", label: "Appartamenti a Dimaro" },
  { slug: "hotel-a-dimaro-folgarida", label: "Dove dormire a Dimaro" },
  { slug: "dormire-vicino-impianti-folgarida-marilleva", label: "Dormire vicino agli impianti" },
  { slug: "vacanze-val-di-sole-inverno", label: "Val di Sole in inverno" },
  { slug: "cosa-fare-a-dimaro-in-estate", label: "Dimaro in estate" },
  { slug: "appartamenti-per-famiglie-val-di-sole", label: "Appartamenti per famiglie" },
];

export default function Home() {
  const { t } = useI18n();
  const [apartments, setApartments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [guides, setGuides] = useState([]);
  const [c, setC] = useState(DEFAULT_CONTENT);
  const [search, setSearch] = useState({ checkin: todayPlus(14), checkout: todayPlus(17), adults: 2, children: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedApt, setSelectedApt] = useState(null);
  const [availResult, setAvailResult] = useState(null); // {ids:[], checkin, checkout}
  const [checking, setChecking] = useState(false);
  const [promos, setPromos] = useState([]);
  const [promoClosed, setPromoClosed] = useState(false);

  const checkAvailability = async () => {
    if (!search.checkin || !search.checkout) return toast.error(t("Seleziona le date di arrivo e partenza"));
    setChecking(true);
    try {
      const { data } = await api.post("/availability/search", {
        checkin: search.checkin, checkout: search.checkout,
        adults: search.adults, children: search.children,
      });
      setAvailResult({ ids: data.available_ids || [], checkin: search.checkin, checkout: search.checkout });
    } catch (e) {
      toast.error(t("Impossibile verificare la disponibilità. Riprova."));
      setAvailResult(null);
    } finally {
      setChecking(false);
      document.getElementById("strutture")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const clearAvail = () => setAvailResult(null);
  const fmtDate = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); } catch { return s; } };
  const shownApartments = availResult ? apartments.filter((a) => availResult.ids.includes(a.id)) : apartments;

  useEffect(() => {
    api.get("/apartments").then((r) => setApartments(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/experiences").then((r) => setExperiences(r.data)).catch(() => {});
    api.get("/blog").then((r) => setGuides(r.data)).catch(() => {});
    api.get("/site-content").then((r) => setC({ ...DEFAULT_CONTENT, ...r.data })).catch(() => {});
    api.get("/discount-codes/banner").then((r) => setPromos(r.data || [])).catch(() => {});
  }, []);

  const openBooking = (apt) => {
    setSelectedApt(apt || apartments[0]);
    setModalOpen(true);
  };

  return (
    <div className="bg-ivory">
      <Seo
        title="Appartamenti in Val di Sole – Case vacanza a Dimaro Folgarida"
        description="Appartamenti in Val di Sole: case vacanza a Dimaro Folgarida (Trentino), ai piedi delle Dolomiti di Brenta e vicino agli impianti di Folgarida-Marilleva. Prenota diretto al miglior prezzo, estate e inverno."
        path="/"
        image={resolveImg(c.hero_image)}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "LodgingBusiness",
              "name": "Appartamenti in Val di Sole",
              "description": c.hero_subtitle,
              "url": SITE_URL + "/",
              "email": c.contact_email,
              "telephone": BUSINESS.phone,
              "priceRange": "€€",
              "address": { "@type": "PostalAddress", "streetAddress": c.contact_address, "addressLocality": "Dimaro Folgarida", "addressRegion": "Trentino-Alto Adige", "postalCode": "38025", "addressCountry": "IT" },
              "areaServed": ["Dimaro", "Folgarida", "Marilleva", "Commezzadura", "Madonna di Campiglio", "Val di Sole", "Dolomiti di Brenta"],
              "geo": { "@type": "GeoCoordinates", "latitude": 46.3167, "longitude": 10.8783 },
              "amenityFeature": [
                { "@type": "LocationFeatureSpecification", "name": "Wi-Fi gratuito", "value": true },
                { "@type": "LocationFeatureSpecification", "name": "Parcheggio privato", "value": true },
                { "@type": "LocationFeatureSpecification", "name": "Ricarica auto elettriche (EV)", "value": true },
                { "@type": "LocationFeatureSpecification", "name": "Adatto alle famiglie", "value": true },
                { "@type": "LocationFeatureSpecification", "name": "Deposito sci riscaldato", "value": true }
              ],
              "aggregateRating": reviews.length ? { "@type": "AggregateRating", "ratingValue": c.rating_value, "reviewCount": reviews.length } : undefined,
            },
            {
              "@type": "ItemList",
              "itemListElement": apartments.map((a, i) => ({
                "@type": "ListItem", "position": i + 1, "name": a.title,
                "url": `${SITE_URL}${aptUrl(a)}`,
              })),
            },
            {
              "@type": "Organization",
              "name": "Appartamenti in Val di Sole",
              "url": SITE_URL + "/",
              "logo": SITE_URL + "/favicon.ico",
              "email": c.contact_email,
              "contactPoint": { "@type": "ContactPoint", "telephone": BUSINESS.phone, "contactType": "reservations", "areaServed": "IT", "availableLanguage": ["Italian", "English", "German"] },
            },
            {
              "@type": "WebSite",
              "name": "Appartamenti in Val di Sole",
              "url": SITE_URL + "/",
              "inLanguage": "it-IT",
            },
            {
              "@type": "FAQPage",
              "mainEntity": HOME_FAQ.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
            },
          ],
        }}
      />
      {/* PROMO BANNER (in cima al sito) */}
      {promos.length > 0 && !promoClosed && (
        <div data-testid="home-promo-banner" className="relative z-50 bg-gold text-fir-deep">
          <div className="max-w-7xl mx-auto px-6 md:px-16 py-2.5 flex items-center justify-center gap-3 text-center">
            <Tag className="shrink-0" size={18} />
            <p className="text-sm md:text-base">
              {promos[0].banner_text ? <span className="font-semibold">{promos[0].banner_text} </span> : null}
              {t("Usa il codice")} <span className="font-mono font-bold px-2 py-0.5 rounded bg-fir-deep text-gold">{promos[0].code}</span> {t("e risparmia")} <span className="font-bold">{promos[0].label}</span>
              {promos[0].remaining != null && (
                <span className="ml-2 opacity-80">· {t("solo per le prime")} {promos[0].max_uses} {t("prenotazioni")} ({promos[0].remaining} {t("rimaste")})</span>
              )}
            </p>
            <button data-testid="home-promo-close" onClick={() => setPromoClosed(true)} aria-label={t("Chiudi banner promozionale")} className="ml-2 opacity-70 hover:opacity-100 transition-opacity shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* HERO */}
      <header className="relative h-screen min-h-[600px] w-full overflow-hidden">
        {/* NAV */}
        <nav className="absolute top-0 inset-x-0 z-40 flex items-center justify-between px-6 md:px-12 py-6">
          <div className="flex items-center gap-2 text-ivory">
            <Mountain className="text-gold" size={28} />
            <span className="font-display text-2xl tracking-tight">Appartamenti in Val di Sole</span>
          </div>
          <div className="flex items-center gap-6 text-ivory/90 text-sm">
            <a href="#strutture" className="hidden md:inline hover:text-gold transition-colors">{t("Strutture")}</a>
            <a href="#esperienze" className="hidden md:inline hover:text-gold transition-colors">{t("Esperienze")}</a>
            <Link to="/guide" className="hidden md:inline hover:text-gold transition-colors">{t("Guide")}</Link>
            <LanguageSwitcher />
            <Link to="/admin/login" data-testid="nav-admin-link" className="hover:text-gold transition-colors">{t("Area Proprietario")}</Link>
          </div>
        </nav>
        <img src={resolveImg(optimizeRemote(c.hero_image, 1920))} alt="Dolomiti di Brenta sopra Dimaro, Val di Sole" fetchpriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-fir-deep/45" />
        <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-16 max-w-4xl">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-gold uppercase tracking-[0.25em] text-xs sm:text-sm mb-3 sm:mb-4">{t(c.hero_eyebrow)}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-white text-4xl sm:text-5xl md:text-7xl leading-[0.98] font-light">
            {t(c.hero_title)} <span className="text-gold">{t(c.hero_title_highlight)}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="text-ivory/85 text-base sm:text-lg mt-4 sm:mt-6 max-w-xl">
            {t(c.hero_subtitle)}
          </motion.p>
        </div>
      </header>

      {/* BOOKING BAR */}
      <div className="relative z-20 px-4 md:px-16 -mt-24 md:-mt-16 mb-2">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
          className="glass rounded-2xl p-4 md:p-5 grid grid-cols-2 gap-3 md:flex md:flex-row md:items-end shadow-2xl max-w-6xl mx-auto">
          <BarField label="Check-in" icon={<CalendarDays size={16} />}>
            <input type="date" data-testid="hero-checkin" aria-label="Data di check-in" value={search.checkin} min={new Date().toISOString().slice(0,10)}
              onChange={(e) => onCheckinChange(e.target.value, search.checkout, setSearch)} className="input-alpine" />
          </BarField>
          <BarField label="Check-out" icon={<CalendarDays size={16} />}>
            <input type="date" data-testid="hero-checkout" aria-label="Data di check-out" value={search.checkout} min={nextDay(search.checkin)}
              onChange={(e) => setSearch({ ...search, checkout: e.target.value })} className="input-alpine" />
          </BarField>
          <BarField label="Adulti" icon={<Users size={16} />}>
            <input type="number" min="1" max="8" data-testid="hero-adults" aria-label="Numero di adulti" value={search.adults}
              onChange={(e) => setSearch({ ...search, adults: Number(e.target.value) })} className="input-alpine" />
          </BarField>
          <BarField label="Bambini <14" icon={<Users size={16} />}>
            <input type="number" min="0" max="8" data-testid="hero-children" aria-label="Numero di bambini sotto i 14 anni" value={search.children}
              onChange={(e) => setSearch({ ...search, children: Number(e.target.value) })} className="input-alpine" />
          </BarField>
          <button data-testid="hero-check-availability" onClick={checkAvailability} disabled={checking}
            className="gold-btn rounded-xl px-8 py-3.5 whitespace-nowrap col-span-2 md:col-span-1 flex items-center justify-center gap-2 disabled:opacity-60">
            {checking && <Loader2 size={16} className="animate-spin" />}{t("Verifica Disponibilità")}</button>
        </motion.div>
      </div>

      <WeatherWidget />

      {/* SEO INTRO */}
      <section className="px-6 md:px-16 pt-14 pb-2 max-w-4xl mx-auto text-center">
        <FadeIn>
          <h2 className="font-display text-3xl md:text-4xl text-fir mb-4">Case vacanza e appartamenti a Dimaro, in Val di Sole</h2>
          <p className="text-fir-deep/75 text-lg leading-relaxed">
            Appartamenti in Val di Sole è la tua base in Trentino per vivere la Val di Sole tutto l'anno: appartamenti indipendenti con cucina, Wi-Fi e parcheggio, ai piedi delle Dolomiti di Brenta. Sei a pochi minuti dagli impianti di Folgarida-Marilleva per la settimana bianca e nel cuore delle esperienze estive, dal rafting sul fiume Noce all'e-bike lungo la ciclabile. Prenota direttamente dal sito ufficiale e ottieni il miglior prezzo, senza commissioni.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {SEO_LINKS.map((l) => (
              <Link key={l.slug} to={`/${l.slug}`} className="px-4 py-2 rounded-full bg-white border border-stone text-fir hover:border-gold hover:text-gold transition-colors text-sm">{l.label}</Link>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* STORYTELLING */}
      <section className="section-pad px-6 md:px-16 grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
        <FadeIn>
          <p className="text-ada-gold uppercase tracking-[0.2em] text-sm mb-4">{t(c.story_eyebrow)}</p>
          <h2 className="font-display text-4xl md:text-5xl text-fir leading-tight mb-6">
            {t(c.story_title)}
          </h2>
          <p className="text-fir-deep/75 text-lg leading-relaxed mb-4">{t(c.story_p1)}</p>
          <p className="text-fir-deep/75 text-lg leading-relaxed">{t(c.story_p2)}</p>
          <div className="flex items-center gap-2 mt-8 text-fir">
            <MapPin size={18} className="text-gold" /> <span>{t(c.story_location)}</span>
          </div>
        </FadeIn>
        <FadeIn delay={0.15}>
          <div className="relative">
            <img src={resolveImg(c.story_image)} alt="Interno appartamento" loading="lazy" className="rounded-3xl w-full h-[460px] object-cover shadow-xl" />
            <div className="absolute -bottom-6 -left-6 glass rounded-2xl px-6 py-4 shadow-lg hidden md:block">
              <p className="font-display text-3xl text-fir">{c.rating_value}<span className="text-gold text-xl">★</span></p>
              <p className="text-xs text-fir-deep/60">{t("Recensioni ospiti")}</p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* PROPERTIES SHOWCASE (Vetrina) */}
      <section id="strutture" className="section-pad px-6 md:px-16 bg-stone/50">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <p className="text-ada-gold uppercase tracking-[0.2em] text-sm mb-3 text-center">{t("Appartamenti · Val di Sole · Trentino")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-fir text-center mb-4">{t("Le nostre strutture")}</h2>
            <p className="text-fir-deep/70 text-center max-w-2xl mx-auto mb-14">{t("Appartamenti indipendenti ai piedi delle Dolomiti di Brenta, ognuno con il suo carattere alpino.")}</p>
          </FadeIn>

          {availResult && (
            <div data-testid="availability-banner" className="max-w-3xl mx-auto -mt-8 mb-10 flex flex-wrap items-center justify-center gap-3 text-center">
              <span className="text-sm text-fir-deep/80">
                {availResult.ids.length > 0
                  ? `${availResult.ids.length} ${availResult.ids.length === 1 ? t("struttura disponibile") : t("strutture disponibili")} · ${fmtDate(availResult.checkin)} → ${fmtDate(availResult.checkout)}`
                  : `${fmtDate(availResult.checkin)} → ${fmtDate(availResult.checkout)}`}
              </span>
              <button data-testid="availability-clear" onClick={clearAvail}
                className="text-sm text-fir underline decoration-gold underline-offset-4 flex items-center gap-1 hover:text-gold transition-colors">
                <XCircle size={15} /> {t("Mostra tutte le strutture")}
              </button>
            </div>
          )}

          {availResult && availResult.ids.length === 0 ? (
            <div data-testid="no-availability" className="max-w-2xl mx-auto text-center bg-white rounded-3xl border border-stone p-10 shadow-sm">
              <CalendarDays size={40} className="text-gold mx-auto mb-4" />
              <h3 className="font-display text-2xl text-fir mb-3">{t("Nessuna disponibilità per queste date")}</h3>
              <p className="text-fir-deep/70 mb-6">{t("Non perdere l'occasione! Prova a modificare le date o il numero di ospiti, oppure apri una singola struttura per vedere i giorni liberi sul calendario e trovare la combinazione perfetta per la tua vacanza in Val di Sole.")}</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button data-testid="no-availability-change" onClick={clearAvail}
                  className="gold-btn px-6 py-3 rounded-xl">{t("Prova altre date")}</button>
              </div>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {shownApartments.map((apt, i) => {
              const isHotel = apt.type === "hotel";
              return (
              <FadeIn key={apt.id} delay={i * 0.08}>
                <div data-testid="apartment-card" className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group h-full flex flex-col">
                  <div className="relative h-64 overflow-hidden">
                    <img src={resolveImg(apt.images?.[0]) || INTERIOR} alt={apt.title} loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-4 left-4 bg-fir/90 text-ivory font-medium px-3 py-1.5 rounded-full text-xs uppercase tracking-wide shadow flex items-center gap-1.5">
                      {isHotel ? <><Mountain size={13} className="text-gold" /> Hotel</> : t("Appartamento")}
                    </div>
                    <div className="absolute top-4 right-4 bg-gold text-fir-deep font-semibold px-4 py-2 rounded-full text-sm shadow">
                      {t("da")} €{apt.from_price}/{t("notte")}
                    </div>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-display text-2xl text-fir mb-1">{t(apt.title)}</h3>
                    <p className="text-fir-deep/60 text-sm mb-4">{t(apt.subtitle)}</p>
                    {isHotel ? (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {(apt.room_types || []).map((rt) => (
                          <span key={rt.id} className="text-xs bg-stone/70 rounded-full px-3 py-1 text-fir-deep/80">{t(rt.name)}</span>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-4 mb-6 text-fir-deep/70 text-sm">
                        {(apt.amenities || []).slice(0, 3).map((a) => {
                          const Icon = AMENITY_ICONS[a] || Sparkles;
                          return <span key={a} className="flex items-center gap-1.5"><Icon size={16} className="text-wood" />{t(a)}</span>;
                        })}
                      </div>
                    )}
                    <div className="flex gap-3 mt-auto">
                      {isHotel ? (
                        <Link to={aptUrl(apt)} data-testid="property-rooms-btn" aria-label={`Scopri le camere di ${apt.title}`}
                          className="gold-btn flex-1 py-3 rounded-xl flex items-center justify-center gap-1.5">
                          {t("Scopri le camere")} <ArrowRight size={16} />
                        </Link>
                      ) : (
                        <>
                          <button data-testid="apartment-book-btn" aria-label={`Prenota ${apt.title}`} onClick={() => openBooking(apt)}
                            className="gold-btn flex-1 py-3 rounded-xl">{t("Prenota ora")}</button>
                          <Link to={aptUrl(apt)} aria-label={`Dettagli di ${apt.title}`}
                            className="flex items-center gap-1 px-5 py-3 rounded-xl border border-wood/50 text-fir hover:bg-fir hover:text-ivory transition-colors">
                            {t("Dettagli")} <ArrowRight size={16} />
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </FadeIn>
            );})}
          </div>
          )}
        </div>
      </section>

      {/* EXPERIENCES - asymmetric bento */}
      <section id="esperienze" className="section-pad px-6 md:px-16 max-w-7xl mx-auto">
        <FadeIn>
          <p className="text-ada-gold uppercase tracking-[0.2em] text-sm mb-3">{t(c.experiences_eyebrow)}</p>
          <h2 className="font-display text-4xl md:text-5xl text-fir mb-4">{t(c.experiences_title)}</h2>
          <p className="text-fir-deep/70 mb-12 max-w-2xl">{t(c.experiences_subtitle)}</p>
        </FadeIn>
        <div className="grid md:grid-cols-3 gap-5 auto-rows-[260px]">
          {experiences.map((exp, i) => (
            <ExperienceCard key={exp.id} exp={exp} big={i === 0} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""} />
          ))}
        </div>
      </section>

      {/* GUIDES */}
      {guides.length > 0 && (
        <section id="guide" className="section-pad px-6 md:px-16 bg-stone/40" data-testid="home-guides-section">
          <div className="max-w-7xl mx-auto">
            <FadeIn>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
                <div>
                  <p className="text-ada-gold uppercase tracking-[0.2em] text-sm mb-3 flex items-center gap-2"><BookOpen size={16} /> {t("Guide & consigli locali")}</p>
                  <h2 className="font-display text-4xl md:text-5xl text-fir">{t("Guide alla Val di Sole")}</h2>
                  <p className="text-fir-deep/70 mt-4 max-w-2xl">{t("Idee, itinerari e consigli pratici per vivere Dimaro, Folgarida-Marilleva e le Dolomiti di Brenta in ogni stagione.")}</p>
                </div>
                <Link to="/guide" data-testid="home-guides-all" className="hidden md:inline-flex items-center gap-1.5 text-gold font-medium hover:gap-2.5 transition-all">{t("Tutte le guide")} <ArrowRight size={16} /></Link>
              </div>
            </FadeIn>
            <div className="grid md:grid-cols-3 gap-6">
              {guides.slice(0, 3).map((g, i) => (
                <FadeIn key={g.id} delay={i * 0.1}>
                  <Link to={`/guide/${g.slug}`} data-testid="home-guide-card"
                    className="bg-white rounded-3xl border border-stone overflow-hidden hover:shadow-xl hover:border-gold/40 transition-all group flex flex-col h-full">
                    {g.cover_image && <img src={resolveImg(g.cover_image)} alt={g.title} loading="lazy" className="h-40 w-full object-cover" />}
                    <div className="p-7 flex flex-col flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(g.keywords || []).slice(0, 2).map((k) => (
                        <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-stone/60 text-fir-deep/70">{k}</span>
                      ))}
                    </div>
                    <h3 className="font-display text-2xl text-fir leading-snug group-hover:text-gold transition-colors">{g.title}</h3>
                    <p className="text-fir-deep/70 mt-3 leading-relaxed flex-1">{g.excerpt || g.meta_description}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-gold font-medium text-sm">{t("Leggi la guida")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
            <div className="mt-8 md:hidden">
              <Link to="/guide" className="inline-flex items-center gap-1.5 text-gold font-medium">{t("Tutte le guide")} <ArrowRight size={16} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="section-pad px-6 md:px-16 bg-fir text-ivory">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <p className="text-gold uppercase tracking-[0.2em] text-sm mb-3 text-center">{t(c.reviews_eyebrow)}</p>
            <h2 className="font-display text-4xl md:text-5xl text-center mb-14">{t(c.reviews_title)}</h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <FadeIn key={r.id} delay={i * 0.1}>
                <div data-testid="review-card" className="glass-dark rounded-2xl p-7 h-full">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={16} className="fill-gold text-gold" />)}
                  </div>
                  <p className="text-ivory/90 leading-relaxed mb-6 italic">"{t(r.text)}"</p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{r.author}</span>
                    <span className="text-xs text-gold uppercase tracking-wide">{r.source}</span>
                  </div>
                  {r.reply && (
                    <div data-testid="review-reply" className="mt-4 pl-3 border-l-2 border-gold">
                      <p className="text-xs uppercase tracking-wide text-gold mb-1">{t("Risposta del proprietario")}</p>
                      <p className="text-ivory/80 text-sm leading-relaxed">{t(r.reply)}</p>
                    </div>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad px-6 md:px-16 bg-stone/40">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <p className="text-ada-gold uppercase tracking-[0.2em] text-sm mb-3 text-center">FAQ</p>
            <h2 className="font-display text-4xl md:text-5xl text-fir text-center mb-10">Domande frequenti</h2>
          </FadeIn>
          <div className="space-y-3">
            {HOME_FAQ.map((f, i) => (
              <details key={i} data-testid="home-faq-item" className="bg-white rounded-2xl border border-stone p-5 group">
                <summary className="font-medium text-fir cursor-pointer list-none flex items-center justify-between">{t(f.q)}<ArrowRight size={18} className="text-gold group-open:rotate-90 transition-transform" /></summary>
                <p className="text-fir-deep/75 mt-3 leading-relaxed">{t(f.a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-fir-deep text-ivory px-6 md:px-16 pt-20 pb-10">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-2 mb-5">
              <Mountain className="text-gold" size={28} />
              <span className="font-display text-3xl">Appartamenti in Val di Sole</span>
            </div>
            <p className="text-ivory/60 leading-relaxed">{t(c.footer_tagline)}</p>
            <div className="flex items-center gap-2 mt-6 text-ivory/50 text-sm">
              <span>{t("Pagamenti sicuri con")}</span>
              <span className="font-bold text-[#003087]">Pay<span className="text-[#009cde]">Pal</span></span>
            </div>
          </div>
          <div>
            <h4 className="font-display text-2xl mb-5 text-gold">{t("Contatti")}</h4>
            <p className="text-ivory/70">{c.contact_email}</p>
            <p className="text-ivory/70 mt-2">{c.contact_address}</p>
            <p className="text-ivory/50 text-sm mt-6">© {new Date().getFullYear()} Appartamenti in Val di Sole · Val di Sole</p>
          <nav className="mt-5 flex flex-col gap-1.5" aria-label="Pagine utili">
            {SEO_LINKS.map((l) => (
              <Link key={l.slug} to={`/${l.slug}`} className="text-ivory/60 hover:text-gold text-sm transition-colors">{l.label}</Link>
            ))}
            <Link to="/guide" className="text-ivory/60 hover:text-gold text-sm transition-colors">Guide alla Val di Sole</Link>
            <Link to="/privacy" className="text-ivory/60 hover:text-gold text-sm transition-colors">Informativa Privacy</Link>
            <Link to="/cookie-policy" className="text-ivory/60 hover:text-gold text-sm transition-colors">Cookie Policy</Link>
          </nav>
          </div>
          <div>
            <h4 className="font-display text-2xl mb-5 text-gold">{t("Dove siamo")}</h4>
            <iframe title="Mappa Dimaro" className="w-full h-48 rounded-2xl border border-ivory/10"
              src={`https://www.google.com/maps?q=${encodeURIComponent(c.map_query)}&output=embed`} loading="lazy" />
          </div>
        </div>
      </footer>

      <ConciergeWidget />
      {selectedApt && (
        <BookingModal open={modalOpen} onClose={() => setModalOpen(false)} apartment={selectedApt} initial={search} />
      )}
    </div>
  );
}

function BarField({ label, icon, children }) {
  const { t } = useI18n();
  return (
    <div className="flex-1">
      <span className="text-xs uppercase tracking-wide text-fir-deep/60 flex items-center gap-1.5 mb-1.5">{icon}{t(label)}</span>
      {children}
    </div>
  );
}

function FadeIn({ children, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay }}>
      {children}
    </motion.div>
  );
}

function ExperienceCard({ exp, className = "", big }) {
  const { t } = useI18n();
  const ICONS = { Inverno: Snowflake, Estate: Waves, Primavera: Trees, Autunno: Trees };
  const Icon = ICONS[exp.category] || Sparkles;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }} transition={{ duration: 0.6 }}
      className={`relative rounded-3xl overflow-hidden group ${className}`} data-testid="experience-card">
      <img src={resolveImg(exp.image) || HERO} alt={exp.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-fir-deep/90 via-fir-deep/30 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-end p-6 text-ivory">
        <div className="flex items-center gap-2 text-gold mb-2">
          <Icon size={big ? 26 : 20} />
          <span className="text-xs uppercase tracking-widest">{t(exp.category)}</span>
        </div>
        <h3 className={`font-display ${big ? "text-4xl" : "text-2xl"}`}>{t(exp.title)}</h3>
        <p className="text-ivory/75 text-sm mt-1 line-clamp-2">{t(exp.description)}</p>
        {exp.partners?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {exp.partners.map((p, i) => (
              <span key={i} data-testid="partner-badge" className="glass-dark rounded-full px-3 py-1 text-xs flex items-center gap-1.5">
                {p.name} {p.discount && <b className="text-gold">{p.discount}</b>}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
